import React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import {
  OriginThemeProvider,
  TrackingProvider,
} from "@origin-digital/ods-core";
import { LinkComponentProps } from "@origin-digital/ods-types";
import { AppContextProvider } from "src/context/AppContext";
import { App } from "src/components/App";
import {
  IInteractionData,
  interaction,
  IPostInteractionState,
  ITrackingProviderCapture,
} from "@origin-digital/reporting-client";
import {
  navigationClose,
  navTo,
  parseMarkdownEvent,
} from "@origin-digital/event-dispatcher";
import { isNative } from "@origin-digital/platform-helpers";
import {
  StepUpGuard,
  useStepUpGate,
  isInternetUser,
  isMasquerading,
} from "@origin-digital/origin-auth";
import { ToggleContextProvider, useToggle } from "@origin-digital/toggle";
import { config } from "src/config";
import { Loader } from "src/components/Loader";
import {
  trackStepUpStart,
  trackStepUpComplete,
} from "src/utils/stepUpTracking";
import { QueryProvider } from "./context/QueryProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  ProfileFieldMapContext,
  ProfileFieldMapProvider,
} from "./crm/ProfileFieldMapContext";
import { ProfileFieldMapMetadata } from "./crm/profileFieldMapContextHelpers";
import { TrackingContextProvider } from "./context/TrackingContext";

import { JointAccountHolderAccessProvider } from "./context/JointAccountHolder/JointAccountHolderAccess";

const REQUIRED_PROFILE_SCOPES = ["write:myprofile", "read:myprofile"];

/**
 * Passed to `useStepUpGate` (its foreground-expiry watcher) and to
 * `<StepUpGuard onTokenExpired>` — fired the moment the elevated access token
 * reaches its `exp`. Closes the profile WebView and — on native only —
 * surfaces the user on the dashboard tab so they have a meaningful landing
 * spot rather than whatever screen happened to be behind the microsite.
 * Desktop web has no native dashboard tab; `navigationClose` alone is
 * sufficient there.
 *
 * Before navigating, `<StepUpGuard>` clears the auth0-spa-js local cache.
 * Origin's Auth0 step-up rule replaces the base-scope token in cache (single
 * entry per `[clientId, audience, scope]`) with a short-lived elevated token,
 * so when it expires `TokenShield`'s next poll uses the expired token against
 * `/api/tokenshield/v1/validate`, the API returns `{valid: false}`, and
 * TokenShield dispatches mesh `LOGOUT` — a full native-app logout, dumping the
 * user back at the login screen. `OriginSpaAuthClient.logout` (which always
 * uses `localOnly: true`) clears that cache entry without ending the underlying
 * Auth0 SSO session, so the next `fetchAccessToken` silent-refreshes against
 * the SSO cookies and returns a fresh base-scope token that `/validate` accepts.
 */
const onStepUpTokenExpired = () => {
  // for web user, don't go back to dashboard
  if (!isNative()) {
    return;
  }
  navigationClose();
  navTo.dashboard();
};

const trackingCaptureWithFieldMapMetadata =
  (metadata: ProfileFieldMapMetadata) =>
  ({
    "data-id": dataId,
    label,
    type,
    postClickState,
  }: ITrackingProviderCapture) => {
    const interactionData: IInteractionData = {
      id: dataId,
      componentType: type,
      label: `${label} ${dataId} ${metadata.type}`,
      state: postClickState as IPostInteractionState,
    };
    interaction("click", interactionData);
  };

const MeshLink = React.forwardRef<any, LinkComponentProps>((props, ref) => {
  const { href, onClick } = parseMarkdownEvent(props.href);
  return (
    <a ref={ref} {...props} href={href} onClick={onClick}>
      {props.children}
    </a>
  );
});

const GatedApp = withRouter((props: RouteComponentProps) => {
  const stepUpAuthEnabled = useToggle("stepUpAuth");
  const gate = useStepUpGate({
    eligibility: (payload) =>
      stepUpAuthEnabled && isInternetUser(payload) && !isMasquerading(payload),
    additionalScopes: REQUIRED_PROFILE_SCOPES,
    environment: config.env,
    onTokenExpired: onStepUpTokenExpired,
  });

  const inner = (
    <ProfileFieldMapProvider>
      <ProfileFieldMapContext.Consumer>
        {(context) => (
          <TrackingProvider
            onTrackingCapture={trackingCaptureWithFieldMapMetadata(
              context.metadata
            )}
          >
            <TrackingContextProvider>
              <JointAccountHolderAccessProvider>
                <App {...props} />
              </JointAccountHolderAccessProvider>
            </TrackingContextProvider>
          </TrackingProvider>
        )}
      </ProfileFieldMapContext.Consumer>
    </ProfileFieldMapProvider>
  );

  if (gate === null) return <Loader isLoading variant="page" />;
  if (!gate) return inner;
  return (
    <StepUpGuard
      additionalScopes={REQUIRED_PROFILE_SCOPES}
      environment={config.env}
      loader={<Loader isLoading variant="page" />}
      onTokenExpired={onStepUpTokenExpired}
      onStepUpStart={trackStepUpStart}
      onStepUpComplete={trackStepUpComplete}
    >
      {inner}
    </StepUpGuard>
  );
});

export const Root = () => (
  <QueryProvider>
    <OriginThemeProvider linkComponent={MeshLink as any} enableThemeSwitching>
      <ToggleContextProvider appName={config.appName} toggles={config.toggles}>
        <ErrorBoundary>
          <AppContextProvider>
            <GatedApp />
          </AppContextProvider>
        </ErrorBoundary>
      </ToggleContextProvider>
    </OriginThemeProvider>
  </QueryProvider>
);
